import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal
} from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

interface ThirdPartyServerItem {
  id: string;
  name: string;
  webhookUrl: string;
  relatedServiceNumberCount: number;
  updatedAt: string;
  description: string;
  appId?: string;
  secret?: string;
}

@Component({
  selector: 'app-tenant-third-party-service-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzAlertModule,
    NzButtonModule,
    NzCardModule,
    NzEmptyModule,
    NzFormModule,
    NzIconModule,
    NzInputModule,
    NzModalModule,
    NzSpinModule,
    NzTableModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './tenant-third-party-service-page.component.html',
  styleUrls: ['./tenant-third-party-service-page.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantThirdPartyServicePageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly http = inject(HttpService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  @ViewChild('pageScrollContainer') pageScrollContainer?: ElementRef<HTMLElement>;
  @ViewChild('tableSectionContainer') tableSectionContainer?: ElementRef<HTMLElement>;

  readonly tenantId = this.route.snapshot.paramMap.get('tenantId') ?? '';
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly saving = signal(false);
  readonly keyword = signal('');
  readonly modalVisible = signal(false);
  readonly secretVisible = signal(false);
  readonly secretAppId = signal('');
  readonly secretValue = signal('');
  readonly secretHintKey = signal('tenant.third-party-service.secret.after-save-hint');
  readonly editingServerId = signal<string | null>(null);
  readonly total = signal(0);
  readonly servers = signal<ThirdPartyServerItem[]>([]);
  private readonly secretCache = new Map<string, { appId?: string; secret: string }>();
  private tableScrollBody?: HTMLElement;
  private readonly tableScrollListener = () => this.handleScrollLoad(this.tableScrollBody);
  pageIndex = 1;
  pageSize = 20;
  refreshTime = Date.now();
  direction: 'asc' | 'desc' = 'desc';
  orderBy = 'updateTime';
  readonly hasMore = computed(() => this.servers().length < this.total());
  readonly filteredServers = computed(() => {
    const key = this.keyword().trim().toLowerCase();
    if (!key) {
      return this.servers();
    }
    return this.servers().filter(item => item.name.toLowerCase().includes(key) || item.webhookUrl.toLowerCase().includes(key));
  });

  readonly serverForm = this.fb.nonNullable.group({
    id: [''],
    name: ['', [Validators.required, Validators.maxLength(100)]],
    webhookUrl: ['', [Validators.required, Validators.pattern(/^https:\/\//i)]],
    description: ['', [Validators.maxLength(500)]]
  });

  ngOnInit(): void {
    this.loadServers();
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.bindTableScrollListener();
      this.tryAutoLoadMore();
    });
  }

  ngOnDestroy(): void {
    this.unbindTableScrollListener();
  }

  loadServers(options: { append?: boolean; pageIndex?: number } = {}): void {
    const append = !!options.append;
    const requestPageIndex = options.pageIndex ?? this.pageIndex;

    if (append) {
      this.loadingMore.set(true);
    } else {
      this.loading.set(true);
    }

    this.http
      .post(ConstantUtil.ADMIN_EXTENSION_SERVER_V1_LIST, {
        pageIndex: Math.max(0, requestPageIndex - 1),
        pageSize: this.pageSize,
        direction: this.direction,
        orderBy: this.orderBy,
        key: this.keyword().trim() || undefined,
        refreshTime: this.refreshTime
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.loadingMore.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            const nextItems = this.normalizeServerList(res.data);
            this.pageIndex = requestPageIndex;
            this.total.set(this.extractTotal(res.data, nextItems.length, append));
            this.servers.set(append ? this.mergeServers(this.servers(), nextItems) : nextItems);
            queueMicrotask(() => {
              this.bindTableScrollListener();
              this.tryAutoLoadMore();
            });
            return;
          }
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.third-party-service.load-failed'));
        },
        error: () => {
          this.message.error(this.i18n.fanyi('tenant.third-party-service.load-failed'));
        }
      });
  }

  openCreateModal(): void {
    this.editingServerId.set(null);
    this.serverForm.reset({
      id: '',
      name: '',
      webhookUrl: '',
      description: ''
    });
    this.modalVisible.set(true);
  }

  openEditModal(item: ThirdPartyServerItem): void {
    this.editingServerId.set(item.id);
    this.loading.set(true);
    this.http
      .post(this.buildPath(ConstantUtil.ADMIN_EXTENSION_SERVER_V1_ITEM, item.id), {})
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            const detail = res.data ?? {};
            this.serverForm.reset({
              id: detail?.id ?? item.id,
              name: detail?.name ?? item.name,
              webhookUrl: detail?.url ?? detail?.webhookUrl ?? item.webhookUrl,
              description: detail?.description ?? item.description ?? ''
            });
            this.modalVisible.set(true);
            return;
          }
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.third-party-service.detail-failed'));
        },
        error: () => {
          this.message.error(this.i18n.fanyi('tenant.third-party-service.detail-failed'));
        }
      });
  }

  closeModal(): void {
    this.modalVisible.set(false);
    this.saving.set(false);
  }

  saveServer(): void {
    if (this.serverForm.invalid) {
      Object.values(this.serverForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      return;
    }

    const formValue = this.serverForm.getRawValue();
    this.saving.set(true);
    this.http
      .post(ConstantUtil.ADMIN_EXTENSION_SERVER_V1_SAVE, {
        id: formValue.id || undefined,
        name: formValue.name.trim(),
        url: formValue.webhookUrl.trim(),
        description: formValue.description.trim() || undefined
      })
      .pipe(
        finalize(() => {
          this.saving.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.modalVisible.set(false);
            this.message.success(
              this.i18n.fanyi(formValue.id ? 'tenant.third-party-service.save-updated' : 'tenant.third-party-service.save-created')
            );
            const savedServerId = res.data?.id ?? formValue.id;
            const appId = res.data?.appId ?? '';
            const secret = this.extractSecret(res.data);
            if (savedServerId && secret) {
              this.secretCache.set(savedServerId, { appId, secret });
              this.openSecretModal(
                {
                  id: savedServerId,
                  name: formValue.name.trim(),
                  webhookUrl: formValue.webhookUrl.trim(),
                  relatedServiceNumberCount: 0,
                  updatedAt: '',
                  description: formValue.description.trim(),
                  appId,
                  secret
                },
                'tenant.third-party-service.secret.after-save-hint'
              );
            }
            this.loadServers();
            return;
          }
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.third-party-service.save-failed'));
        },
        error: () => {
          this.message.error(this.i18n.fanyi('tenant.third-party-service.save-failed'));
        }
      });
  }

  updateKeyword(value: string): void {
    const nextValue = value ?? '';
    if (nextValue === this.keyword()) {
      return;
    }
    this.keyword.set(nextValue);
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadServers();
  }

  onPageScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    this.handleScrollLoad(target);
  }

  deleteServer(item: ThirdPartyServerItem): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('tenant.third-party-service.delete-title'),
      nzContent: this.i18n.fanyi('tenant.third-party-service.delete-content'),
      nzOkText: this.i18n.fanyi('common.delete'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOkDanger: true,
      nzOnOk: () =>
        new Promise<void>(resolve => {
          this.http.post(this.buildPath(ConstantUtil.ADMIN_EXTENSION_SERVER_V1_DELETE, item.id), {}).subscribe({
            next: res => {
              if (res.success) {
                this.message.success(this.i18n.fanyi('tenant.third-party-service.delete-success'));
                this.loadServers();
                resolve();
                return;
              }
              showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.third-party-service.delete-failed'));
              resolve();
            },
            error: () => {
              this.message.error(this.i18n.fanyi('tenant.third-party-service.delete-failed'));
              resolve();
            }
          });
        })
    });
  }

  viewSecret(item: ThirdPartyServerItem): void {
    if (!item.secret) {
      this.message.warning(this.i18n.fanyi('tenant.third-party-service.secret.unavailable'));
      return;
    }

    this.openSecretModal(item, 'tenant.third-party-service.secret.view-hint');
  }

  copySecret(): void {
    const secret = this.secretValue().trim();
    if (!secret) {
      this.message.error(this.i18n.fanyi('common.copy-failed'));
      return;
    }

    const nav = navigator as Navigator & {
      clipboard?: {
        writeText(text: string): Promise<void>;
      };
    };

    if (nav.clipboard?.writeText) {
      nav.clipboard
        .writeText(secret)
        .then(() => this.message.success(this.i18n.fanyi('common.copy-success')))
        .catch(() => this.copySecretWithFallback(secret));
      return;
    }

    this.copySecretWithFallback(secret);
  }

  closeSecretModal(): void {
    this.secretVisible.set(false);
    this.secretAppId.set('');
    this.secretValue.set('');
  }

  private loadMore(): void {
    if (this.loading() || this.loadingMore() || !this.hasMore()) {
      return;
    }

    this.loadServers({
      append: true,
      pageIndex: this.pageIndex + 1
    });
  }

  private normalizeServerList(data: any): ThirdPartyServerItem[] {
    const items = Array.isArray(data) ? data : (data?.items ?? data?.list ?? data?.records ?? []);
    const emptyText = this.i18n.fanyi('common.none');
    return items.map((item: any, index: number) => {
      const id = item?.id ?? item?.serverId ?? `server-${index}`;
      const secretEntry = this.secretCache.get(id);
      return {
        id,
        name: item?.name ?? item?.serverName ?? emptyText,
        webhookUrl: item?.url ?? item?.webhookUrl ?? emptyText,
        relatedServiceNumberCount: Number(item?.relatedServiceNumberCount ?? item?.bindingCount ?? item?.serviceNumberCount ?? 0),
        updatedAt: this.formatDateTime(item?.updateTime ?? item?.updatedAt ?? item?.gmtModified),
        description: item?.description ?? '',
        appId: item?.appId ?? secretEntry?.appId,
        secret: this.extractSecret(item) || secretEntry?.secret
      };
    });
  }

  private mergeServers(current: ThirdPartyServerItem[], incoming: ThirdPartyServerItem[]): ThirdPartyServerItem[] {
    const merged = [...current];
    const existingIds = new Set(current.map(item => item.id));

    incoming.forEach(item => {
      if (existingIds.has(item.id)) {
        const index = merged.findIndex(server => server.id === item.id);
        if (index >= 0) {
          merged[index] = item;
        }
        return;
      }
      existingIds.add(item.id);
      merged.push(item);
    });

    return merged;
  }

  private extractTotal(data: any, itemCount: number, append: boolean): number {
    const total = Number(data?.totalCount ?? data?.total ?? data?.count);
    if (Number.isFinite(total) && total >= 0) {
      return total;
    }
    return append ? Math.max(this.total(), this.servers().length + itemCount) : itemCount;
  }

  private openSecretModal(item: ThirdPartyServerItem, hintKey: string): void {
    this.secretHintKey.set(hintKey);
    this.secretAppId.set(item.appId ?? '');
    this.secretValue.set(item.secret ?? '');
    this.secretVisible.set(true);
  }

  private copySecretWithFallback(secret: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = secret;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      const ok = document.execCommand('copy');
      if (ok) {
        this.message.success(this.i18n.fanyi('common.copy-success'));
      } else {
        this.message.error(this.i18n.fanyi('common.copy-failed'));
      }
    } catch {
      this.message.error(this.i18n.fanyi('common.copy-failed'));
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private formatDateTime(value: unknown): string {
    if (value == null || value === '') {
      return this.i18n.fanyi('common.none');
    }
    if (typeof value === 'string' && !/^\d+$/.test(value)) {
      return value;
    }

    const date = new Date(Number(value));
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return new Intl.DateTimeFormat(this.i18n.currentLang || undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(date);
  }

  private buildPath(template: string, id: string): string {
    return template.replace('{id}', encodeURIComponent(id));
  }

  private extractSecret(data: any): string {
    return data?.secret ?? data?.newSecret ?? data?.appSecret ?? '';
  }

  private tryAutoLoadMore(): void {
    const container = this.tableScrollBody ?? this.pageScrollContainer?.nativeElement;
    if (!container || this.loading() || this.loadingMore() || !this.hasMore()) {
      return;
    }

    // If the first page does not fill the viewport, keep loading until scrolling becomes possible.
    if (container.scrollHeight <= container.clientHeight + 1) {
      this.loadMore();
    }
  }

  private handleScrollLoad(container?: HTMLElement | null): void {
    if (!container) {
      return;
    }

    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (remaining <= 80) {
      this.loadMore();
    }
  }

  private bindTableScrollListener(): void {
    const nextBody = this.tableSectionContainer?.nativeElement.querySelector('.ant-table-body') as HTMLElement | null;
    if (nextBody === this.tableScrollBody) {
      return;
    }

    this.unbindTableScrollListener();
    if (!nextBody) {
      return;
    }

    nextBody.addEventListener('scroll', this.tableScrollListener, { passive: true });
    this.tableScrollBody = nextBody;
  }

  private unbindTableScrollListener(): void {
    if (!this.tableScrollBody) {
      return;
    }

    this.tableScrollBody.removeEventListener('scroll', this.tableScrollListener);
    this.tableScrollBody = undefined;
  }
}
